import { useCallback, useState } from "react";

import type { CardListView } from "../components/CardsListFiltersBar";
import type { CardListSortBy, CardListSortOrder } from "../types/cards-api.types";

const STORAGE_KEY = "coloca:cards:list-preferences";

export interface CardListPreferences {
  sort_by: CardListSortBy;
  order: CardListSortOrder;
  view: CardListView;
}

const DEFAULTS: CardListPreferences = {
  sort_by: "created_at",
  order: "DESC",
  view: "list",
};

function readPreferences(): CardListPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<CardListPreferences>;
    return {
      sort_by: parsed.sort_by === "balance" ? "balance" : "created_at",
      order: parsed.order === "ASC" ? "ASC" : "DESC",
      view: parsed.view === "grid" ? "grid" : "list",
    };
  } catch {
    return DEFAULTS;
  }
}

export function useCardListPreferences() {
  const [preferences, setPreferencesState] = useState<CardListPreferences>(() => readPreferences());

  const update = useCallback((patch: Partial<CardListPreferences>) => {
    setPreferencesState((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // private mode / quota: keep the in-memory value, skip persistence
      }
      return next;
    });
  }, []);

  return { preferences, update };
}
